// src/pages/Home.tsx
import React from "react";
import {getAccessToken, getIdToken} from "../auth";
import {decodeJwt} from "../jwt";


export default function Home() {
    const idToken = getIdToken();
    const claims = idToken ? decodeJwt(idToken) : null;

    const accessToken = getAccessToken(); // <-- you probably meant this, not getIdToken()
    // const accessClaims = accessToken ? decodeJwt(accessToken) : null;

    const email = claims?.email ?? claims?.["cognito:username"] ?? null;

    const revolutions = [
        {
            title: "Mechanization",
            subtitle: "Industry 1.0",
            period: "~1760–1840",
            bullets: ["Water & steam power", "Mechanical production", "Early factories"],
        },
        {
            title: "Electrification",
            subtitle: "Industry 2.0",
            period: "~1870–1914",
            bullets: ["Electric power", "Assembly lines", "Mass production & standardization"],
        },
        {
            title: "Automation & IT",
            subtitle: "Industry 3.0",
            period: "~1970s–2000s",
            bullets: ["Electronics & computers", "PLCs/SCADA/CNC", "Robotics & automation"],
        },
        {
            title: "Integration & Intelligence",
            subtitle: "Industry 4.0",
            period: "~2010–present",
            bullets: ["IIoT connectivity", "Data-driven optimization", "IT/OT + value-chain integration"],
        },
    ] as const;

    return (
        <div style={{padding: 24, maxWidth: 1100, margin: "0 auto"}}>
            <header style={{marginBottom: 16}}>
                <h1   style={{
                    margin: 0,
                    fontSize: "2.5rem",      // ~40px
                    fontWeight: 700,
                    letterSpacing: "-0.02em",
                }}>Industry 4.0</h1>

            </header>

            <section style={{marginTop: 24}}>
                <h2 style={{margin: "0 0 12px 0"}}>The Fourth Industrial Revolutions</h2>

                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                        gap: 12,
                    }}
                >
                    {revolutions.map((r) => (
                        <div
                            key={r.title}
                            style={{
                                border: "1px solid rgba(0,0,0,0.12)",
                                borderRadius: 12,
                                padding: 14,
                                background: "rgba(255,255,255,0.03)",
                            }}
                        >
                            <div style={{display: "flex", justifyContent: "space-between", gap: 8}}>
                                <div>
                                    <div style={{fontWeight: 700}}>{r.title}</div>
                                    <div style={{opacity: 0.85}}>{r.subtitle}</div>
                                </div>
                                <div style={{fontSize: 12, opacity: 0.7, textAlign: "right"}}>{r.period}</div>
                            </div>

                            <ul style={{margin: "10px 0 0 18px", padding: 0, lineHeight: 1.4}}>
                                {r.bullets.map((b) => (
                                    <li key={b}>{b}</li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                <p style={{marginTop: 12, opacity: 0.8}}>
                    Industry 4.0 isn’t one tool—it’s the degree of <strong>digital integration</strong> across
                    operations,
                    people, and partners.
                </p>
            </section>

            <div
                style={{
                    marginTop: 24,
                    padding: 16,
                    border: "1px solid rgba(0,0,0,0.12)",
                    borderRadius: 12,
                    opacity: 0.92,
                }}
            >
                <p style={{ marginTop: 0 }}>
                    This self-assessment tool is an independent, informational resource intended solely to help organizations
                    reflect on their current Industry 4.0 capabilities and readiness. It is designed as a{" "}
                    <strong>low-overhead, self-paced pre-assessment</strong> that teams can complete at their own convenience—
                    starting, pausing, and returning later as needed—so that more stakeholders can contribute without requiring
                    a multi-day onsite effort. The goal is to help you build a shared baseline and identify practical next steps,
                    including whether a more formal, in-depth assessment would be valuable.
                </p>

                <p style={{ marginTop: 8 }}>
                    This tool is informed in part by concepts and principles described in the publicly available Smart Industry
                    Readiness Index (SIRI), originally developed by Singapore’s Economic Development Board and now stewarded
                    globally by the International Centre for Industrial Transformation (INCIT).
                </p>

                <p style={{ marginTop: 8 }}>
                    This tool does not provide an official SIRI assessment and is not endorsed, authorized, licensed, or
                    approved by INCIT, the Singapore Economic Development Board, or any of their partners, and does
                    not replace or replicate the official SIRI assessment process. Use of this tool does not result in
                    any form of SIRI certification, accreditation, rating, or formal benchmark, nor should the results
                    be interpreted as equivalent to those produced through an official SIRI assessment or engagement.
                </p>

                <p style={{ marginTop: 8 }}>
                    Many organizations choose to review their results with a facilitator or advisor to help interpret findings,
                    align stakeholders, and translate insights into a practical improvement roadmap. If you’d like help
                    discussing your results, identifying priority focus areas, or connecting your readiness insights to
                    concrete next steps, our team can support facilitated review and planning sessions.
                </p>

                <div style={{ fontSize: 12, opacity: 0.65, marginTop: 8 }}>
                    © {new Date().getFullYear()} Rochester Institute of Technology. All rights reserved.
                    <br/>
                    “SIRI” and “Smart Industry Readiness Index” are trademarks or registered trademarks
                    of their respective owners, including the International Centre for Industrial
                    Transformation (INCIT).
                </div>

            </div>

        </div>
    );
}


